import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DataGrid, GridColDef, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { AppLogDoc } from '../DiagnosticDashboard';

interface PaginationModel {
  page: number;
  pageSize: number;
}

interface LogTableProps {
  rows: AppLogDoc[];
  total: number;
  loading: boolean;
  paginationModel: PaginationModel;
  onPaginationModelChange: (model: PaginationModel) => void;
  onRowClick: (log: AppLogDoc) => void;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onDeleteSelected: () => void;
}

const LEVEL_COLORS: Record<string, string> = {
  error: '#DC2626',
  warn: '#D97706',
  info: '#2563EB',
};

const columns: GridColDef[] = [
  {
    field: 'createdAt',
    headerName: 'Timestamp',
    width: 180,
    valueGetter: (value: any) => {
      return value ? new Date(value).toLocaleString() : 'Invalid Date';
    },
  },
  {
    field: 'category',
    headerName: 'Category',
    width: 150,
  },
  {
    field: 'level',
    headerName: 'Level',
    width: 90,
    renderCell: (params: any) => (
      <span style={{ color: LEVEL_COLORS[params.value] ?? '#374151', fontWeight: 600 }}>
        {params.value}
      </span>
    ),
  },
  {
    field: 'message',
    headerName: 'Message',
    flex: 1,
    minWidth: 200,
  },
  {
    field: 'orgId',
    headerName: 'Org',
    width: 160,
    valueGetter: (value: any) => value ?? '—',
  },
  {
    field: 'userEmail',
    headerName: 'User',
    width: 200,
    valueGetter: (value: any) => value ?? '—',
  },
];

export default function LogTable({
  rows,
  total,
  loading,
  paginationModel,
  onPaginationModelChange,
  onRowClick,
  selectedIds,
  onSelectionChange,
  onDeleteSelected,
}: LogTableProps) {
  return (
    <Box
      sx={{
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        bgcolor: 'background.paper',
      }}
    >
      {selectedIds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1,
            borderBottom: '1px solid #E5E7EB',
            bgcolor: '#FEF2F2',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedIds.length} selected
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={onDeleteSelected}
          >
            Delete selected
          </Button>
        </Box>
      )}
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row._id}
        loading={loading}
        rowCount={total}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[25, 50, 100]}
        onRowClick={(params: GridRowParams) => onRowClick(params.row as AppLogDoc)}
        checkboxSelection
        rowSelectionModel={selectedIds}
        onRowSelectionModelChange={(model: GridRowSelectionModel) =>
          onSelectionChange(model as string[])
        }
        sx={{
          border: 'none',
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .MuiDataGrid-row:hover': { bgcolor: 'action.hover' },
        }}
        autoHeight
        disableRowSelectionOnClick
        keepNonExistentRowsSelected
      />
    </Box>
  );
}
